import * as React from 'react';

import type {
  ClientCampaignHistoryItemDto,
  ClientDetailDto,
} from '@/api/clients-client';
import { useClientCampaigns, useClientDetail } from '@/api/use-clients';
import { Sheet } from '@/components/ui/sheet';
import { StatusBadge } from '@/components/ui/status-badge';
import { clientStateLabel } from '@/lib/client-labels';

// =============================================================================
// client-detail-panel.tsx — Volet latéral FICHE CLIENT (DBU §2.3 F-C03, l.784/826).
// =============================================================================
// Volet (Sheet, R22-FICHE-DRAWER) ancré à droite, la liste reste visible derrière. Ouvert par l'action
// « Consulter la fiche » d'une ligne du référentiel (clients-list-screen). Trois sections :
//   1. Dossier sociétaire enrichi (data.gouv) — DIFFÉRÉ VISIBLE : on affiche les attributs OWNED déjà
//      connus + un libellé « enrichissement à venir ». On N'INVENTE AUCUNE donnée data.gouv tant que
//      S23-CLIENT-ENRICH n'est pas livré (DDD §3.3, no-fabrication).
//   2. Attributs métier + Contact référent (PII informationnelle, §3.3.5 inv.2) — VISIBLE UNIQUEMENT
//      dans le volet, JAMAIS dans la liste (le read-model liste ne porte aucune PII).
//   3. Chronologie des campagnes liées — tableau LECTURE SEULE, ordre SERVEUR (« plus récente d'abord »,
//      cross-BC campaign via Published Language). JAMAIS 404 : liste vide = « aucune campagne ».
//
// ZÉRO règle métier : l'autorité (contenu de la fiche, tri chronologie, RBAC) est l'API. L'UI relaie.
//
// WCAG 2.1 AA : le Sheet porte role="dialog"/aria-modal + focus-trap + Échap ; ce contenu porte ses
// PROPRES titres (<h2 id> reliés par aria-labelledby aux <section>) ; la chronologie est un <table> avec
// <caption> + <th scope> ; les états sont rendus en LIBELLÉ texte (badge = renfort couleur, jamais seul).

/** Formate un ISO-8601 en date FR lisible (jamais « Invalid Date »). `null` → « — ». */
function formatDateFr(iso: string | null): string {
  if (iso === null) {
    return '—';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

/** Valeur d'attribut ou repli « — » (attribut non saisi / enrichissement à venir). */
function orDash(value: string | null): string {
  return value !== null && value.length > 0 ? value : '—';
}

interface ClientDetailPanelProps {
  /** SIREN sélectionné (fiche ouverte) ; `null` = volet fermé. Contrôlé par la liste. */
  readonly siren: string | null;
  /** Ferme le volet (déselectionne). */
  readonly onClose: () => void;
}

export function ClientDetailPanel({
  siren,
  onClose,
}: ClientDetailPanelProps): React.JSX.Element {
  const detail = useClientDetail(siren);
  const campaigns = useClientCampaigns(siren);

  return (
    <Sheet
      open={siren !== null}
      onClose={onClose}
      label={
        detail.data !== undefined
          ? `Fiche client ${detail.data.raisonSociale}`
          : 'Fiche client'
      }
    >
      {detail.isPending ? (
        <p role="status" className="text-muted-foreground">
          Chargement de la fiche client…
        </p>
      ) : detail.isError ? (
        <p role="alert" className="text-destructive">
          {detail.error.message}
        </p>
      ) : (
        <div className="space-y-8">
          <FicheHeader detail={detail.data} />
          <DossierSocietaireSection detail={detail.data} />
          <AttributsContactSection detail={detail.data} />
          <ChronologieSection
            isPending={campaigns.isPending}
            isError={campaigns.isError}
            errorMessage={campaigns.error?.message}
            rows={campaigns.data ?? []}
          />
        </div>
      )}
    </Sheet>
  );
}

function FicheHeader({ detail }: { readonly detail: ClientDetailDto }): React.JSX.Element {
  const state = clientStateLabel(detail.state);
  return (
    <header className="space-y-1">
      <h2 className="text-xl font-semibold tracking-tight">{detail.raisonSociale}</h2>
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span className="font-mono">{detail.siren}</span>
        <StatusBadge tone={state.tone}>{state.label}</StatusBadge>
      </div>
    </header>
  );
}

/** Section 1 — Dossier sociétaire (attributs OWNED + enrichissement data.gouv DIFFÉRÉ visible). */
function DossierSocietaireSection({
  detail,
}: {
  readonly detail: ClientDetailDto;
}): React.JSX.Element {
  return (
    <section aria-labelledby="fiche-dossier-heading" className="space-y-3">
      <h3 id="fiche-dossier-heading" className="text-lg font-medium">
        Dossier sociétaire
      </h3>
      <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Raison sociale" value={detail.raisonSociale} />
        <Field label="SIREN" value={detail.siren} mono />
        <Field label="Secteur d'activité" value={detail.secteurActivite} />
        <Field label="Référence interne JEKO" value={orDash(detail.referenceInterneJeko)} />
      </dl>
      <p className="text-xs text-muted-foreground">
        Enrichissement du dossier sociétaire (données publiques data.gouv) à venir.
      </p>
    </section>
  );
}

/** Section 2 — Attributs métier + contact référent (PII fiche-only, §3.3.5 inv.2). */
function AttributsContactSection({
  detail,
}: {
  readonly detail: ClientDetailDto;
}): React.JSX.Element {
  return (
    <section aria-labelledby="fiche-attributs-heading" className="space-y-4">
      <h3 id="fiche-attributs-heading" className="text-lg font-medium">
        Attributs métier et contact référent
      </h3>
      <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Segment" value={orDash(detail.segment)} />
        <Field label="Taille" value={orDash(detail.taille)} />
        <Field label="Dernière modification" value={formatDateFr(detail.lastModifiedAt)} />
        <Field label="Auteur de la modification" value={detail.lastModifiedByLabel} />
      </dl>

      <div className="space-y-2">
        <h4 className="text-sm font-medium">Contacts référents</h4>
        {detail.contactsReferents.length === 0 ? (
          <p role="status" className="text-sm text-muted-foreground">
            Aucun contact référent renseigné.
          </p>
        ) : (
          <ul className="space-y-3">
            {detail.contactsReferents.map((contact) => (
              <li
                key={contact.referentId}
                className="rounded-md border border-border p-3 text-sm"
              >
                <dl className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                  <Field label="Nom" value={orDash(contact.nom)} />
                  <Field label="Fonction" value={orDash(contact.fonction)} />
                  <Field label="Courriel" value={orDash(contact.email)} />
                  <Field label="Téléphone" value={orDash(contact.telephone)} />
                </dl>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

/** Section 3 — Chronologie des campagnes liées (cross-BC campaign, lecture seule, ordre serveur). */
function ChronologieSection({
  isPending,
  isError,
  errorMessage,
  rows,
}: {
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly errorMessage: string | undefined;
  readonly rows: readonly ClientCampaignHistoryItemDto[];
}): React.JSX.Element {
  return (
    <section aria-labelledby="fiche-campagnes-heading" className="space-y-3">
      <h3 id="fiche-campagnes-heading" className="text-lg font-medium">
        Campagnes liées
      </h3>
      {isPending ? (
        <p role="status" className="text-muted-foreground">
          Chargement des campagnes…
        </p>
      ) : isError ? (
        <p role="alert" className="text-destructive">
          {errorMessage ?? 'Les campagnes liées ne sont pas disponibles pour le moment.'}
        </p>
      ) : rows.length === 0 ? (
        <p role="status" className="text-muted-foreground">
          Ce client n'est lié à aucune campagne de référencement.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="ds-table w-full border-collapse text-sm">
            <caption className="sr-only">
              Chronologie des campagnes de référencement liées à ce client (intitulé, état, dates
              de début et de fin, nombre de fournisseurs), de la plus récente à la plus ancienne.
            </caption>
            <thead>
              <tr className="border-b border-border bg-muted/40 text-left text-muted-foreground">
                <th scope="col" className="px-4 py-3 font-medium">
                  Campagne
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  État
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  Début
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  Fin
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  Fournisseurs
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.campaignId} className="border-b border-border last:border-b-0">
                  <th scope="row" className="px-4 py-3 text-left align-top font-medium">
                    {row.intitule}
                  </th>
                  <td className="px-4 py-3 align-top">{row.state}</td>
                  <td className="px-4 py-3 align-top text-muted-foreground">
                    {formatDateFr(row.startDate)}
                  </td>
                  <td className="px-4 py-3 align-top text-muted-foreground">
                    {formatDateFr(row.endDate)}
                  </td>
                  <td className="px-4 py-3 align-top tabular-nums">{row.supplierCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** Paire libellé/valeur d'une <dl> (WCAG : <dt>/<dd> explicites). */
function Field({
  label,
  value,
  mono = false,
}: {
  readonly label: string;
  readonly value: string;
  readonly mono?: boolean;
}): React.JSX.Element {
  return (
    <div className="space-y-0.5">
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className={mono ? 'font-mono' : undefined}>{value}</dd>
    </div>
  );
}
